// @ts-strict-ignore
import React, { useCallback } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { DragDropProvider } from '@dnd-kit/react';
import * as BookmarkTypes from 'common/types/reports/bookmark';
import type { DragEndEvent } from 'common/components/draggable-list/types';
import { findKeyByValue } from 'common/util/typescript';
import { useTranslation } from 'common/i18n/hooks';
import { handleOnBeforeDrag } from 'common/components/draggable-list/utils';
import { reporter } from 'common/report/error-reporting';
import type { ReportState } from '../report-bookmark-manager';
import { handleFilterAcrossMetricEvents, handleGlobalFilerToMetricEvent, handleMetricEventFiltersRearrange, handleMetricEventsRearrange } from './metric-events';
import { handleGlobalFiltersRearrange, handleGlobalFilterToBreakdowns, handleInlineFilterToBreakdowns, handleInlineFilterToGlobalFilters } from './filters';
import { handleMetricsRearrange } from './metrics';
import { BookmarkDragOperationMetadata, BookmarkDragOperationProps } from './types';
import { handleBreakdownsRearrange, handleBreakdownToFilter } from './breakdowns';

/**
 * DragDropProvider for handling drag and drop operations related to bookmarks in reports.
 * This takes care of interactions like dragging a filter onto a metric event, rearranging different sections throughout the report, etc...
 */
export function BookmarkDragDropProvider(props: {
  report: ReportState;
  children: React.ReactNode;
}) {
  const i18n = useTranslation();
  const {
    updateParams
  } = props.report;
  const params = cloneDeep(props.report.params);
  const onDragEnd = useCallback((event: DragEndEvent<BookmarkDragOperationMetadata>) => {
    var _event$nativeEvent, _event$operation$targ, _event$operation$sour, _targetData$behaviorI, _sourceData$behaviorI;
    const isCloning = Boolean((_event$nativeEvent = event.nativeEvent as MouseEvent) === null || _event$nativeEvent === void 0 ? void 0 : _event$nativeEvent.altKey);
    const targetData = ((_event$operation$targ = event.operation.target) === null || _event$operation$targ === void 0 ? void 0 : _event$operation$targ.data) as BookmarkDragOperationMetadata;
    const sourceData = ((_event$operation$sour = event.operation.source) === null || _event$operation$sour === void 0 ? void 0 : _event$operation$sour.data) as BookmarkDragOperationMetadata;
    if (event.operation.canceled || !sourceData || !targetData) {
      return;
    }
    mixpanel.track("[mp-query-builder] Drag operation", {
      sourceData,
      targetData
    });
    const operationProps: BookmarkDragOperationProps = {
      i18n,
      event,
      params,
      updateParams,
      sourceData,
      targetData,
      isCloning
    };

    /** rearrange metrics */
    if (findKeyByValue(BookmarkTypes.TopLevelMetricType, sourceData.type) && findKeyByValue(BookmarkTypes.TopLevelMetricType, targetData.type)) {
      return handleMetricsRearrange(operationProps);
    }

    /** perform an event filter drop onto an event inside a metric */
    if (sourceData.type === "inline-filter" && targetData.type === "behavior-event") {
      return handleFilterAcrossMetricEvents(operationProps);
    }

    /** handles an inline filter onto a the global filter section */
    if (sourceData.type === "inline-filter" && targetData.type === "global-filters") {
      return handleInlineFilterToGlobalFilters({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }

    /** perform a global filter drop onto an event inside a metric */
    if (sourceData.type === "global-filter" && targetData.type === "behavior-event") {
      return handleGlobalFilerToMetricEvent(operationProps);
    }

    /** rearrange global filters section */
    if (sourceData.type === "global-filter" && targetData.type === "global-filter") {
      return handleGlobalFiltersRearrange(operationProps);
    }

    /** rearrange events inside a metric */
    if (sourceData.type === "behavior-event" && targetData.type === "behavior-event" && targetData.group === sourceData.group) {
      handleMetricEventsRearrange({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData
      });
    }

    /** rearrange filters inside a metric event */
    if (sourceData.type === "inline-filter" && targetData.type === "inline-filter" && ((_targetData$behaviorI = targetData.behaviorIndex) === null || _targetData$behaviorI === void 0 ? void 0 : _targetData$behaviorI.toString()) === ((_sourceData$behaviorI = sourceData.behaviorIndex) === null || _sourceData$behaviorI === void 0 ? void 0 : _sourceData$behaviorI.toString()) && targetData.group === sourceData.group) {
      handleMetricEventFiltersRearrange({
        i18n,
        event,
        params,
        updateParams,
        sourceData,
        targetData,
        isCloning
      });
    }

    /** handles a global filter onto the breakdown section */
    if (sourceData.type === "global-filter" && targetData.type === "global-breakdowns") {
      handleGlobalFilterToBreakdowns(operationProps);
    }

    /** handles an inline filter onto the breakdown section */
    if (sourceData.type === "inline-filter" && targetData.type === "global-breakdowns") {
      handleInlineFilterToBreakdowns(operationProps);
    }

    /** rearrange global breakdown section */
    if (sourceData.type === "breakdown" && targetData.type === "breakdown") {
      return handleBreakdownsRearrange(operationProps);
    }

    /** handles a breakdown onto a filter section */
    if (sourceData.type === "breakdown" && (targetData.type === "global-filters" || targetData.type === "behavior-event")) {
      return handleBreakdownToFilter(operationProps);
    }
  }, [props.report]);
  return <DragDropProvider onDragOver={event => {
    event.preventDefault();
  }} onBeforeDragStart={handleOnBeforeDrag} onDragEnd={event => {
    // we don't want to block the UI whenever a UI exception happens
    try {
      onDragEnd(event);
    } catch (error) {
      var _event$operation$sour2, _event$operation$targ2;
      reporter.error("[mp-query-builder] Failed Drag Operation", {
        error: String(error),
        sourceData: (_event$operation$sour2 = event.operation.source) === null || _event$operation$sour2 === void 0 ? void 0 : _event$operation$sour2.data,
        targetData: (_event$operation$targ2 = event.operation.target) === null || _event$operation$targ2 === void 0 ? void 0 : _event$operation$targ2.data
      });
    }
  }} data-sentry-element="DragDropProvider" data-sentry-component="BookmarkDragDropProvider" data-sentry-source-file="provider.tsx">
      {props.children}
    </DragDropProvider>;
}